// ═══════════════════════════════════════════════════════════════════════════════
// Consent / PICS Page — Full-screen, non-skippable
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../components/ui/ToastProvider';
import { api, legalDocumentApi } from '../../lib/api';
import { APP_NAME } from '@fdh/shared';
import { resolveLegalDocumentLocale } from '@fdh/shared';
import type { ConsentStatusResponse, ConsentAcceptResponse, LegalDocumentPublic } from '@fdh/shared';
import { getConsentChromeContent } from '../../i18n/consentChrome';
import { NotificationBell } from '../../components/NotificationBell';
import { MainMenuButton } from '../../components/MainMenuButton';

const renderLegalMarkdown = (content: string) =>
  content.split('\n').map((line, index) => {
    const key = `${index}-${line.slice(0, 12)}`;
    if (line.trim() === '') {
      return <div key={key} className="h-3" aria-hidden />;
    }
    if (line.startsWith('### ')) {
      return (
        <h2 key={key} className="mb-2 mt-5 text-sm font-bold text-gray-900 first:mt-0 sm:text-base">
          {line.replace(/^###\s+/, '')}
        </h2>
      );
    }
    return (
      <p key={key} className="whitespace-pre-line text-sm leading-relaxed text-gray-700">
        {line}
      </p>
    );
  });

export const ConsentPage = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { language, t } = useLanguage();
  const { showSuccessToast } = useToast();
  const scrollRef = useRef<HTMLDivElement>(null);
  const isFirstLanguageEffect = useRef(true);

  const [agreed, setAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [consentVersion, setConsentVersion] = useState('');
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [picsDocument, setPicsDocument] = useState<LegalDocumentPublic | null>(null);

  useEffect(() => {
    if (isFirstLanguageEffect.current) {
      isFirstLanguageEffect.current = false;
      return;
    }
    setAgreed(false);
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    setHasScrolledToBottom(false);
  }, [language]);

  // Check consent status on mount
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const documentResponse = await legalDocumentApi.getActive('pics', resolveLegalDocumentLocale(language));
        if (documentResponse.success && documentResponse.data) {
          setPicsDocument(documentResponse.data);
        }
      } catch {
        // Fallback to bundled PICS content if DB content is unavailable.
      }

      try {
        const response = await api.get<ConsentStatusResponse>('/consent/status');
        if (response.success && response.data) {
          setConsentVersion(response.data.currentVersion);
          if (response.data.hasConsented) {
            navigate('/', { replace: true });
            return;
          }
        }
      } catch {
        // Continue to show consent page
      }
      setIsLoading(false);
    };

    checkStatus();
  }, [language, navigate]);

  // Track scroll to bottom of PICS content
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 30;
    if (atBottom) {
      setHasScrolledToBottom(true);
    }
  }, []);

  const handleToggleAgreed = useCallback(() => {
    setAgreed((prev) => !prev);
  }, []);

  const handleAccept = useCallback(async () => {
    if (!agreed || isSubmitting) return;

    setIsSubmitting(true);
    setError('');

    try {
      const response = await api.post<ConsentAcceptResponse>('/consent/accept', {
        locale: language,
      });

      if (!response.success) {
        throw new Error(response.error ?? 'Failed to accept consent');
      }

      await refreshUser();
      showSuccessToast(t('toast.consent.acceptSuccess'));
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept consent');
      setIsSubmitting(false);
    }
  }, [agreed, isSubmitting, language, navigate, refreshUser]);

  const content = getConsentChromeContent(language);
  const pageTitle = picsDocument?.title ?? content.pageTitle;
  const displayVersion = consentVersion || picsDocument?.version || 'v1';

  if (isLoading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-gray-50"
        role="status"
        aria-label={t('common.loading')}
      >
        <div className="text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
          <p className="mt-4 text-sm text-gray-500">{content.loadingText}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      {/* Header */}
      <header className="z-10 border-b border-gray-200 bg-white shadow-sm">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-600">
              <span className="text-sm font-bold text-white">{APP_NAME.charAt(0)}</span>
            </div>
            <span className="text-sm font-semibold text-gray-700">{APP_NAME}</span>
          </div>

          <div className="flex items-center gap-2">
            <MainMenuButton />
            <NotificationBell />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex flex-1 flex-col">
        <div className="mx-auto w-full max-w-2xl flex-1 px-4 py-6">
          {/* Title */}
          <div className="mb-4 text-center">
            <div className="mb-2 inline-flex items-center rounded-full bg-amber-100 px-3 py-1">
              <svg
                className="mr-1.5 h-4 w-4 text-amber-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
              <span className="text-xs font-medium text-amber-700">
                {content.versionLabel}: {displayVersion}
              </span>
            </div>
            <h1
              className="text-xl font-bold text-gray-900 sm:text-2xl"
              role="heading"
              aria-level={1}
            >
              {pageTitle}
            </h1>
            <p className="mt-1 text-sm text-gray-500">{content.subtitle}</p>
          </div>

          {/* PICS Content — Scrollable */}
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="mb-4 max-h-[50vh] overflow-y-auto rounded-xl border border-gray-200 bg-white p-5 shadow-sm sm:max-h-[55vh] sm:p-6"
            tabIndex={0}
            role="document"
            aria-label={pageTitle}
          >
            {picsDocument
              ? renderLegalMarkdown(picsDocument.content)
              : content.sections.map((section, idx) => (
                  <div key={idx} className={idx > 0 ? 'mt-5' : ''}>
                    <h2
                      className="mb-2 text-sm font-bold text-gray-900 sm:text-base"
                      role="heading"
                      aria-level={2}
                    >
                      {section.title}
                    </h2>
                    <div className="whitespace-pre-line text-sm leading-relaxed text-gray-700 sm:text-sm">
                      {section.content}
                    </div>
                  </div>
                ))}
          </div>

          {/* Scroll hint */}
          {!hasScrolledToBottom && (
            <p className="mb-3 text-center text-xs text-gray-400">{content.scrollHint}</p>
          )}

          {/* Consent Checkbox */}
          <div className="mb-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <label
              className="flex cursor-pointer items-start gap-3"
              tabIndex={0}
              role="checkbox"
              aria-checked={agreed}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleToggleAgreed();
                }
              }}
            >
              <input
                type="checkbox"
                checked={agreed}
                onChange={handleToggleAgreed}
                className="mt-0.5 h-5 w-5 flex-shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                aria-label={content.checkboxLabel}
              />
              <span className="text-sm leading-relaxed text-gray-700">
                {content.checkboxLabel}
              </span>
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div
              className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3"
              role="alert"
            >
              <div className="flex items-center gap-2">
                <svg
                  className="h-4 w-4 flex-shrink-0 text-red-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Accept Button */}
          <button
            type="button"
            onClick={handleAccept}
            disabled={!agreed || isSubmitting}
            className={`w-full rounded-lg px-6 py-4 text-base font-medium transition-all focus:ring-2 focus:ring-offset-2 focus:outline-none ${
              agreed && !isSubmitting
                ? 'bg-primary-600 text-white shadow-md hover:bg-primary-700 focus:ring-primary-500'
                : 'cursor-not-allowed bg-gray-200 text-gray-400'
            }`}
            tabIndex={0}
            aria-label={content.buttonLabel}
            aria-disabled={!agreed || isSubmitting}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                {content.loadingText}
              </span>
            ) : (
              content.buttonLabel
            )}
          </button>

          {/* Data notice footer */}
          <p className="mt-4 text-center text-xs text-gray-400">{content.dataNoticeFooter}</p>
        </div>
      </main>
    </div>
  );
};
